import React, {useCallback, useEffect, useState} from "react";
import DynamicRows from "../../../../../components/dynamic-rows";
import SelectInput from "../../../../../components/forms/select";
import TextBox from "../../../../../components/forms/text-box";
import {useEventBus} from "../../../../../helpers/hooks/event-bus";
import {useAppInfo} from "../../../../../helpers/hooks/common-hook";
import {WORKFLOW_OPTION_SLOT} from "../../const/settings-const";
import {BUTTON_GO_TO_STEP_RULE_NAME} from "../../const/settings-type";
import journeyStmStore from "../../service/journey-stm-store";
import SlotSelector from "../slot-selector";
import TextArea from "../../../../../components/forms/text-area";
import {fileUpload, fileUploadUrlGet} from "../../../../../services/contents";
import MultiLang from "../multi-lang";

const WEB_URL_TYPE = 'web_url';
const SET_VALUE_TYPE = 'set_value';
const GO_TO_STEP_TYPE = 'go_to_step';
const SET_INPUT_TYPE = 'set_input';
const END_CHAT_TYPE = 'end_chat';

const getCurrentSteps = myId => {
    return Object.keys(journeyStmStore.getAll()).filter(id => id !== myId).map(id => ({
        id,
        name: journeyStmStore.get(id).stateConfig.name
    }));
}

function SetValueOptions({rootClassName = "flex flex-col", defaultSlot, button, metadata, handleInputChange}) {
    return (
        <div className={rootClassName}>
            {(!defaultSlot || (defaultSlot === WORKFLOW_OPTION_SLOT)) &&
                <SlotSelector
                    rootClass="pt-4"
                    title="Variable"
                    value={button.slot}
                    autoFillFromEntity={false}
                    slots={metadata.slots}
                    onChange={handleInputChange}/>
            }
            <div className="mt-4">
                <TextBox
                    label="Value"
                    type="text"
                    name="payload"
                    placeholder="Enter Value"
                    value={button.payload}
                    onChange={handleInputChange}
                />
            </div>
        </div>
    )
}

function IconUploader({value, onChange}) {
    const {appId} = useAppInfo();
    const [fileObj, setFileObj] = useState();
    const [filePreviewUrl, setFilePreviewUrl] = useState(value);
    const [state, setState] = useState({ processing: false, success: false, failed: false, message: '' });

    useEffect(() => { setFilePreviewUrl(value); }, [value]);

    const handleSelectFile = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        const file = files[0];
        const allowed = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/x-icon',
            'image/vnd.microsoft.icon'
        ];
        const nameAllowed = (fname) => /\.(jpe?g|png|gif|ico)$/i.test(fname || '');
        if (!(allowed.includes(file.type) || nameAllowed(file.name))) {
            alert('Please select a JPG, PNG, GIF, or ICO file.');
            event.target.value = '';
            return;
        }
        if (file.size > 1048576) {
            alert('File must be 1 MB or smaller.');
            event.target.value = '';
            return;
        }
        setFileObj(file);
        const reader = new FileReader();
        reader.onload = () => setFilePreviewUrl(reader.result);
        reader.readAsDataURL(file);
    }

    useEffect(() => {
        const upload = async () => {
            if (!fileObj || state.processing) return;
            setState({ processing: true, success: false, failed: false, message: '' });
            try {
                const fileExt = (fileObj.name.split('.').pop() || '').toLowerCase();
                const contentType = fileObj.type || 'image/jpeg';
                const form = { fileExt, contentType, resourceType: 'workflow-card' };
                const {response, status} = await fileUploadUrlGet(appId, form);
                if (status === 200) {
                    const {uploadUrl, contentPath} = response;
                    await fileUpload(uploadUrl, fileObj);
                    onChange && onChange(contentPath);
                    setState({ processing: false, success: true, failed: false, message: '' });
                } else {
                    throw new Error('Upload URL generation failed');
                }
            } catch (err) {
                console.error('Icon upload failed', err);
                setState({ processing: false, success: false, failed: true, message: 'Upload failed' });
            }
        };
        upload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileObj]);

    return (
        <div className="flex items-center gap-3">
            {filePreviewUrl && <img src={filePreviewUrl} alt="" className="h-8 w-8 rounded" />}
            <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/x-icon,image/vnd.microsoft.icon,.jpg,.jpeg,.png,.gif,.ico"
                onChange={handleSelectFile}
            />
            {value && (
                <button
                    type="button"
                    className="text-xs text-red-600"
                    onClick={() => onChange('')}
                >
                    Clear
                </button>
            )}
            {state.processing && <span className="text-xs text-gray-500">Uploading...</span>}
            {state.failed && <span className="text-xs text-red-600">{state.message}</span>}
        </div>
    )
}

const ActionButtons = React.memo(({
                                      stepId, dataField, configs, defaultSlot, metadata, onChange = () => {
    }, displayTopBorder = true
                                  }) => {
    const renderRow = useCallback((button, handleInputChange, rawId) => {

        button.actionType = button.actionType || SET_VALUE_TYPE;


        if(button.actionType === END_CHAT_TYPE){
            button.payload = END_CHAT_TYPE
        }

        return <div className="flex flex-col gap-2" key={rawId}>
            <TextBox
                rootClass=""
                label="*Display Text"
                type="text"
                name="title"
                placeholder="Enter Display Text"
                value={button.title}
                onChange={handleInputChange}
            />
            <MultiLang
                rootClassName="pt-2"
                value={button.lang_title || {}}
                name="lang_title"
                responseModifyByAI={false}
                onChangeData={handleInputChange}
                showBottomDivider={true}
                    />
            <TextArea
                rootClass=""
                label="Description"
                type="text"
                name="description"
                parameterName="description"
                placeholder="Enter Description"
                value={button.description}
                onChange={handleInputChange}
            />
            <MultiLang
                rootClassName="pt-2"
                value={button.lang_description || {}}
                name="lang_description"
                parameterName="lang_description"
                responseModifyByAI={false}
                onChangeData={handleInputChange}
                showBottomDivider={true}
                    />
            <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700">Icon</label>
                <IconUploader
                    value={button.icon}
                    onChange={(val) => handleInputChange({ target: { name: 'icon', value: val } })}
                />
            </div>
            <SelectInput
                label="Action"
                id="actionType"
                name="actionType"
                autoComplete="actionType"
                value={button.actionType}
                onChange={handleInputChange}
                optionsComp={<>
                    <option value={SET_VALUE_TYPE}>Set Variable Value</option>
                    <option value={SET_INPUT_TYPE}>Set Input Value</option>
                    <option value={WEB_URL_TYPE}>Open URL</option>
                    <option value={GO_TO_STEP_TYPE}>Go To Step</option>
                    <option value={END_CHAT_TYPE}>End Chat</option>
                </>}
            />

            {button.actionType === SET_VALUE_TYPE &&
                <SetValueOptions
                    button={button}
                    metadata={metadata}
                    handleInputChange={handleInputChange}
                    defaultSlot={defaultSlot}
                />
            }

            {button.actionType === WEB_URL_TYPE &&
                <div className="flex flex-col gap-2">
                    <TextBox
                        rootClass="pt-4"
                        label="URL"
                        type="url"
                        name="url"
                        placeholder="Enter Url"
                        value={button.url}
                        onChange={handleInputChange}
                    />
                    <SetValueOptions
                        button={button}
                        metadata={metadata}
                        handleInputChange={handleInputChange}
                        defaultSlot={defaultSlot}
                    />
                </div>
            }

            {button.actionType === GO_TO_STEP_TYPE &&
                <SelectInput
                    rootClass="pt-2 pb-4"
                    label="Step"
                    required
                    name="goToStep"
                    value={button.goToStep}
                    onChange={event => {
                        handleInputChange(event);
                        // onChangeGoToStep(event)
                    }}
                    optionsComp={<>
                        <option className="text-gray-500" value="" selected={true}>Select step</option>
                        {getCurrentSteps(stepId).map((state, si) => (
                            <option key={si} value={state.id}>{state.name}</option>
                        ))}
                    </>}
                />
            }

            {button.actionType === SET_INPUT_TYPE &&
                <TextBox
                    rootClass="pt-4"
                    label="Input Value"
                    type="text"
                    name="inputValue"
                    placeholder="Enter Value"
                    value={button.inputValue}
                    onChange={handleInputChange}
                />
            }
        </div>
    }, [])
    return (
        <div className="flex flex-col pt-4 gap-2 pb-4">
            <DynamicRows
                title="Buttons"
                className=""
                data={configs}
                onChange={onChange}
                defaultThumbnail="Button"
                showTopDivider={displayTopBorder}
                showBottomDivider={false}
                rowsTemplate={renderRow}
                rowAccordingPanel={true}
            />
        </div>
    )
})

export default ActionButtons
